import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import firebase from "firebase/app";
import "firebase/analytics";
import { PosthogService } from './posthog.service';

// Initialize Firebase if not already initialized
if (!firebase.apps.length) {
  firebase.initializeApp(environment.firebase);
}

// Initialize Firebase Analytics
const analytics = firebase.analytics();

@Injectable({
  providedIn: 'root'
})
export class EventsService {

  constructor(private posthogService: PosthogService) {
    // Enable debug mode if environment variable is set
    if (environment.enableDebug) {
      console.log('🔥 Firebase Analytics Debug Mode Enabled');
      
      // Enable debug mode - multiple approaches
      (window as any).gtag_enable_debug_mode = true;
      
      // Enable debug mode via gtag config
      if ((window as any).gtag) {
        (window as any).gtag('config', 'G-C6ZN8GKHY3', {
          debug_mode: true
        });
        console.log('✅ Debug mode set via gtag config');
      }
    } else {
      // Production mode - disable debug logging
      console.log('🚀 Firebase Analytics Production Mode');
      
      // Ensure debug mode is disabled
      (window as any).gtag_enable_debug_mode = false;
      
      // Configure for production
      if ((window as any).gtag) {
        (window as any).gtag('config', 'G-C6ZN8GKHY3', {
          debug_mode: false,
          send_page_view: true,
          anonymize_ip: true // GDPR compliance
        });
      }
    }
    
    // Always enable analytics collection
    try {
      analytics.setAnalyticsCollectionEnabled(true);
      if (environment.enableDebug) {
        console.log('✅ Analytics collection enabled');
      }
    } catch (e) {
      if (environment.enableDebug) {
        console.log('Analytics collection setup:', e);
      }
    }
    
    if (environment.enableDebug) {
      console.log('🔥 Firebase Analytics Service initialized');
      console.log('🔍 Measurement ID: G-C6ZN8GKHY3');
      console.log('🔍 Analytics instance:', analytics);
    }
  }

  /**
   * Example usage:
   * this.eventsService.setUserId('user_123');
   */
  setUserId(uid: string) {
    console.log("🔥 Firebase Analytics - Setting user ID:", uid);
    try {
      analytics.setUserId(uid);
      this.posthogService.identify(uid);
      console.log("✅ Firebase Analytics - User ID set successfully");
    } catch (error) {
      console.error("❌ Firebase Analytics - Failed to set user ID:", error);
    }
  }

  /**
   * Example usage:
   * this.eventsService.setUserProperty('favorite_food', 'pizza');
   */
  setUserProperty(name: string, value: string) {
    console.log("🔥 Firebase Analytics - Setting user property:", name, value);
    try {
      analytics.setUserProperties({ [name]: value });
      this.posthogService.setUserProperty(name, value);
      console.log("✅ Firebase Analytics - User property set successfully");
    } catch (error) {
      console.error("❌ Firebase Analytics - Failed to set user property:", error);
    }
  }

  registerSuperProperties(properties: { [key: string]: any }) {
    try {
      this.posthogService.registerSuperProperties(properties);
    } catch (error) {
      console.error("❌ Failed to register super properties:", error);
    }
  }

  /**
   * A general-purpose logEvent method, if you want to use custom event names
   * and parameters without creating a separate method for each.
   */
  logCustomEvent(eventName: string, params: any = {}) {
    if (environment.enableDebug) {
      console.log('🔥 Firebase Analytics - Logging event:', eventName, params);
    }
    
    try {
      (analytics as any).logEvent(eventName, params);
      this.posthogService.capture(eventName, params);
      
      if (environment.enableDebug) {
        console.log('✅ Firebase Analytics - Event sent successfully:', eventName);
      }
    } catch (error) {
      // Always log errors, even in production
      console.error('❌ Firebase Analytics - Event failed:', eventName, error);
    }
  }

  /**
   * Screen names, session timeouts, etc.
   */
  setScreenName(screenName: string) {
    console.log('📱 Firebase Analytics - Setting screen name:', screenName);
    try {
      (analytics as any).logEvent('screen_view', {
        screen_name: screenName
      });
      this.posthogService.setScreenName(screenName);
      console.log('✅ Firebase Analytics - Screen name set:', screenName);
    } catch (error) {
      console.error('❌ Firebase Analytics - Screen name failed:', screenName, error);
    }
  }

  clearUserContext() {
    this.posthogService.reset();
  }

  setSessionTimeoutDuration(duration: number) {
    // This is handled automatically by Firebase Analytics
    console.log('📱 Firebase Analytics - Session timeout:', duration);
  }

  /* ------------------------------------------------------------------------
   * BELOW ARE EXAMPLE METHODS FOR SPECIFIC EVENTS YOU MENTIONED
   * ------------------------------------------------------------------------
   */

  /* ----------------------
   * 1. Virality Events
   * ---------------------- */
  logInviteInitiated(inviteMethod: string, referralCode?: string) {
    this.logCustomEvent('invite_initiated', {
      invite_method: inviteMethod,
      referral_code: referralCode,
    });
  }

  logInviteAccepted(referrerId: string, referralCode?: string) {
    this.logCustomEvent('invite_accepted', {
      referrer_id: referrerId,
      referral_code: referralCode,
    });
  }

  logWaitingListSignup(canSkipLine: boolean, referralCode?: string) {
    this.logCustomEvent('waiting_list_signup', {
      can_skip_line: canSkipLine,
      referral_code: referralCode,
    });
  }

  logSocialLinkClicked(platform: string, campaignId?: string) {
    this.logCustomEvent('social_link_clicked', {
      platform: platform,
      campaign_id: campaignId,
    });
  }

  /* ----------------------
   * 2. User Journey Events
   * ---------------------- */
  logUserSignup(signupMethod: string, referralCode?: string) {
    this.logCustomEvent('sign_up', {
      method: signupMethod,
      referral_code: referralCode,
    });
  }

  logUserLogin(loginMethod: string) {
    this.logCustomEvent('login', {
      method: loginMethod,
    });
  }

  logOnboardingStepCompleted(stepName: string, stepNumber: number) {
    this.logCustomEvent('onboarding_step_completed', {
      step_name: stepName,
      step_number: stepNumber,
    });
  }

  logOnboardingCompleted(totalSteps: number, completionTimeSeconds: number) {
    this.logCustomEvent('onboarding_completed', {
      total_steps: totalSteps,
      completion_time_seconds: completionTimeSeconds,
    });
  }

  /* ----------------------
   * 3. Product/Feature Usage Events
   * ---------------------- */
  logFeatureUsed(featureName: string, context?: string) {
    this.logCustomEvent('feature_used', {
      feature_name: featureName,
      context: context,
    });
  }

  logWorkflowCreated(workflowType: string, complexity?: string) {
    this.logCustomEvent('workflow_created', {
      workflow_type: workflowType,
      complexity: complexity,
    });
  }

  logAgentInteraction(agentType: string, interactionType: string, duration?: number) {
    this.logCustomEvent('agent_interaction', {
      agent_type: agentType,
      interaction_type: interactionType,
      duration: duration,
    });
  }

  /* ----------------------
   * 4. Content/Engagement Events
   * ---------------------- */
  logContentViewed(contentType: string, contentId: string, durationSeconds?: number) {
    this.logCustomEvent('content_viewed', {
      content_type: contentType,
      content_id: contentId,
      duration_seconds: durationSeconds,
    });
  }

  logTutorialStarted(tutorialName: string) {
    this.logCustomEvent('tutorial_begin', {
      tutorial_name: tutorialName,
    });
  }

  logTutorialCompleted(tutorialName: string, completionTimeSeconds: number) {
    this.logCustomEvent('tutorial_complete', {
      tutorial_name: tutorialName,
      completion_time_seconds: completionTimeSeconds,
    });
  }

  /* ----------------------
   * 5. Conversion/Business Events
   * ---------------------- */
  logSubscriptionStarted(planName: string, planPrice: number, currency: string) {
    this.logCustomEvent('purchase', {
      currency: currency,
      value: planPrice,
      items: [{
        item_id: planName,
        item_name: planName,
        price: planPrice,
        quantity: 1
      }]
    });
  }

  logTrialStarted(planName: string, trialDurationDays: number) {
    this.logCustomEvent('trial_started', {
      plan_name: planName,
      trial_duration_days: trialDurationDays,
    });
  }

  logTrialConverted(planName: string, planPrice: number, currency: string) {
    this.logCustomEvent('trial_converted', {
      plan_name: planName,
      plan_price: planPrice,
      currency: currency,
    });
  }

  /* ----------------------
   * 6. Support/Help Events
   * ---------------------- */
  logSupportTicketCreated(category: string, priority: string) {
    this.logCustomEvent('support_ticket_created', {
      category: category,
      priority: priority,
    });
  }

  logHelpArticleViewed(articleId: string, category: string) {
    this.logCustomEvent('help_article_viewed', {
      article_id: articleId,
      category: category,
    });
  }

  logChatbotInteraction(query: string, wasHelpful: boolean) {
    this.logCustomEvent('chatbot_interaction', {
      query_type: query,
      was_helpful: wasHelpful,
    });
  }

  /* ----------------------
   * 7. Error/Performance Events
   * ---------------------- */
  logError(errorType: string, errorMessage: string, context?: string) {
    this.logCustomEvent('app_error', {
      error_type: errorType,
      error_message: errorMessage,
      context: context,
    });
  }

  logPerformanceIssue(issueType: string, loadTimeMs: number, context?: string) {
    this.logCustomEvent('performance_issue', {
      issue_type: issueType,
      load_time_ms: loadTimeMs,
      context: context,
    });
  }

  /* ----------------------
   * 8. Marketing/Campaign Events
   * ---------------------- */
  logCampaignClick(campaignId: string, source: string, medium: string) {
    this.logCustomEvent('campaign_click', {
      campaign_id: campaignId,
      source: source,
      medium: medium,
    });
  }

  logLandingPageView(campaignId: string, landingPageUrl: string) {
    this.logCustomEvent('landing_page_view', {
      campaign_id: campaignId,
      landing_page_url: landingPageUrl,
    });
  }

  logEmailOpened(campaignId: string, emailType: string) {
    this.logCustomEvent('email_opened', {
      campaign_id: campaignId,
      email_type: emailType,
    });
  }

  /* ----------------------
   * 9. A/B Testing Events
   * ---------------------- */
  logExperimentViewed(experimentId: string, variant: string) {
    this.logCustomEvent('experiment_viewed', {
      experiment_id: experimentId,
      variant: variant,
    });
  }

  logExperimentConversion(experimentId: string, variant: string, conversionType: string) {
    this.logCustomEvent('experiment_conversion', {
      experiment_id: experimentId,
      variant: variant,
      conversion_type: conversionType,
    });
  }

  /* ----------------------
   * 10. Retention/Engagement Events
   * ---------------------- */
  logDailyActiveUser() {
    this.logCustomEvent('daily_active_user', {
      timestamp: new Date().toISOString(),
    });
  }

  logSessionDuration(durationSeconds: number) {
    this.logCustomEvent('session_duration', {
      duration_seconds: durationSeconds,
    });
  }

  logUserRetention(daysSinceSignup: number, isActive: boolean) {
    this.logCustomEvent('user_retention', {
      days_since_signup: daysSinceSignup,
      is_active: isActive,
    });
  }

  /* ----------------------
   * Additional Methods for Backwards Compatibility
   * ---------------------- */
  logContentShared(platform: string, contentType: string) {
    this.logCustomEvent('share', {
      method: platform,
      content_type: contentType,
    });
  }

  logSkipLineAttempt(attempts: number) {
    this.logCustomEvent('skip_line_attempt', {
      attempt_number: attempts,
    });
  }

  logFirstAgentCreated(agentType: string) {
    this.logCustomEvent('first_agent_created', {
      agent_type: agentType,
    });
  }

  logUpgradeAttempt(targetPlan: string) {
    this.logCustomEvent('upgrade_attempt', {
      target_plan: targetPlan,
    });
  }

  logSubscriptionStart(planName: string) {
    this.logCustomEvent('subscription_start', {
      plan_name: planName,
    });
  }
}